import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/api';
import useGameStore from '../../stores/gameStore';

export interface GuestAdventureInviteRow {
  inviteId: string;
  guestUserId: string;
  createdByUserId?: string;
  adventureId: string;
  guestSessionId: string;
  initialVariables?: Record<string, string>;
  status: 'active' | 'expired' | 'revoked';
  createdAt: string;
  redeemedAt?: string | null;
}

export interface GuestAdventureListProps {
  sessionId?: string;
  onInviteClick?: (inviteId: string) => void;
  /** Scoped shared variable name; row status is read from its per-user map entry. */
  statusVariable?: string;
  /** Comma-separated value:Label pairs; use an empty value (e.g. `:Awaiting response`) for missing entries. */
  statusLabels?: string;
  /** Only show guests whose shared status equals this value (requires statusVariable). */
  statusFilter?: string;
  className?: string;
  style?: React.CSSProperties;
  customColor?: string;
}

const DEFAULT_PENDING_STATUS_LABEL = 'Awaiting response';

const getColorWithAlpha = (color: string, alpha: string) => {
  if (color.startsWith('#')) {
    return `${color}${alpha}`;
  }
  return color;
};

const getDisplayLabel = (invite: GuestAdventureInviteRow): string => {
  const name =
    invite.initialVariables?.guest_name?.trim() ||
    invite.initialVariables?.invitee_name?.trim();
  if (name) {
    return name;
  }
  return `Guest ${invite.guestUserId.substring(0, 8)}`;
};

const getInviteStatusLabel = (status: GuestAdventureInviteRow['status']): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'expired':
      return 'Expired';
    case 'revoked':
      return 'Revoked';
    default:
      return status;
  }
};

export const parseStatusLabelMap = (raw?: string): Map<string, string> => {
  const labels = new Map<string, string>();
  if (!raw?.trim()) {
    return labels;
  }

  for (const part of raw.split(',')) {
    const piece = part.trim();
    if (!piece) {
      continue;
    }
    const colonIndex = piece.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }
    const key = piece.slice(0, colonIndex).trim();
    const label = piece.slice(colonIndex + 1).trim();
    if (label) {
      labels.set(key, label);
    }
  }

  return labels;
};

export const resolveGuestStatusLabel = (
  value: string | undefined,
  statusLabels?: string,
  defaultPendingLabel = DEFAULT_PENDING_STATUS_LABEL
): string => {
  const normalized = (value ?? '').trim();
  const labels = parseStatusLabelMap(statusLabels);

  if (labels.has(normalized)) {
    return labels.get(normalized)!;
  }
  if (!normalized && labels.has('')) {
    return labels.get('')!;
  }
  if (!normalized) {
    return defaultPendingLabel;
  }
  if (labels.size === 0) {
    return normalized;
  }
  return normalized;
};

export const parseSharedStatusMap = (
  results: Record<string, string | number>,
  variableName: string
): Record<string, string> => {
  const key = `shared_${variableName}_map`;
  const raw = results[key];
  if (typeof raw !== 'string' || !raw.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const map: Record<string, string> = {};
    for (const [userId, status] of Object.entries(parsed)) {
      if (typeof status === 'string') {
        map[userId] = status;
      } else if (status !== undefined && status !== null) {
        map[userId] = String(status);
      }
    }
    return map;
  } catch {
    return {};
  }
};

export const filterInvitesBySharedStatus = (
  invites: GuestAdventureInviteRow[],
  statusMap: Record<string, string>,
  statusFilter?: string
): GuestAdventureInviteRow[] => {
  if (!statusFilter) {
    return invites;
  }
  return invites.filter(
    (invite) => (statusMap[invite.guestUserId] ?? '').trim() === statusFilter
  );
};

/** Guest list UI only shows invites the host can still manage. */
export const filterListableGuestAdventureInvites = (
  invites: GuestAdventureInviteRow[]
): GuestAdventureInviteRow[] => invites.filter((invite) => invite.status === 'active');

export const GuestAdventureList: React.FC<GuestAdventureListProps> = ({
  sessionId,
  onInviteClick,
  statusVariable,
  statusLabels,
  statusFilter,
  className = '',
  style,
  customColor
}) => {
  const { currentSession } = useGameStore();
  const [invites, setInvites] = useState<GuestAdventureInviteRow[]>([]);
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeSessionId = sessionId || currentSession?.id;

  const getRowStatusLabel = useCallback(
    (invite: GuestAdventureInviteRow): string => {
      if (statusVariable) {
        return resolveGuestStatusLabel(statusMap[invite.guestUserId], statusLabels);
      }
      return getInviteStatusLabel(invite.status);
    },
    [statusLabels, statusMap, statusVariable]
  );

  const loadInvites = useCallback(async () => {
    if (!activeSessionId) {
      setInvites([]);
      setStatusMap({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const rows = await apiService.listGuestAdventures(activeSessionId);
      setInvites(filterListableGuestAdventureInvites(rows));

      if (statusVariable) {
        const query = await apiService.querySharedVariables(activeSessionId, [
          { variableName: statusVariable, aggregation: 'map' }
        ]);
        setStatusMap(parseSharedStatusMap(query.results, statusVariable));
      } else {
        setStatusMap({});
      }
    } catch (err) {
      console.error('Failed to load guest adventures:', err);
      setError(err instanceof Error ? err.message : 'Failed to load guest adventures');
      setInvites([]);
      setStatusMap({});
    } finally {
      setIsLoading(false);
    }
  }, [activeSessionId, statusVariable]);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  const visibleInvites = filterInvitesBySharedStatus(invites, statusMap, statusFilter);
  const showStatusLabel = !statusFilter;

  if (isLoading) {
    return (
      <div
        className={`guest-adventure-list loading ${className}`}
        style={style}
        data-testid="guest-adventure-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading guest adventures...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`guest-adventure-list error ${className}`}
        style={style}
        data-testid="guest-adventure-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (visibleInvites.length === 0) {
    return (
      <div
        className={`guest-adventure-list empty ${className}`}
        style={style}
        data-testid="guest-adventure-list-empty"
      >
        <div className="flex items-center justify-center py-2" data-testid="empty-guest-adventure-list">
          <p className="italic text-center text-gray-400">
            {statusFilter ? 'No matching guests' : 'No guest adventures for this session'}
          </p>
        </div>
      </div>
    );
  }

  const renderStatusLabel = (invite: GuestAdventureInviteRow) =>
    showStatusLabel ? (
      <span className="text-sm italic text-gray-300">{getRowStatusLabel(invite)}</span>
    ) : null;

  if (!onInviteClick) {
    return (
      <div className={`guest-adventure-list ${className}`} style={style} data-testid="guest-adventure-list">
        <div className="flex flex-col" style={{ gap: '0.5rem' }}>
          {visibleInvites.map((invite) => (
            <div
              key={invite.inviteId}
              className="guest-adventure-item-card"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '0.75rem 1rem'
              }}
              data-testid={`guest-adventure-card-${invite.inviteId}`}
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-base text-white">{getDisplayLabel(invite)}</span>
                {renderStatusLabel(invite)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`guest-adventure-list selectable ${className}`}
      style={style}
      data-testid="guest-adventure-list-selectable"
    >
      <div className="guest-adventure-list-items flex flex-col" style={{ gap: '0.5rem' }}>
        {visibleInvites.map((invite) => {
          const buttonStyle: React.CSSProperties = {};
          if (customColor) {
            buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
            buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
          }

          return (
            <button
              key={invite.inviteId}
              type="button"
              onClick={() => onInviteClick(invite.inviteId)}
              className="guest-adventure-item-button inventory-item-button w-full"
              style={buttonStyle}
              data-testid={`guest-adventure-item-${invite.inviteId}`}
              data-invite-id={invite.inviteId}
              aria-label={`Select guest ${getDisplayLabel(invite)}`}
            >
              <div className="flex items-center w-full" style={{ padding: '0.75rem 1rem' }}>
                <div className="flex-1 flex items-center">
                  <span className="text-base">{getDisplayLabel(invite)}</span>
                </div>
                <div className="flex-shrink-0">{renderStatusLabel(invite)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GuestAdventureList;
